import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { FiPlus, FiEye, FiEdit, FiTrash2, FiBarChart2, FiUsers, FiShare2, FiFolder, FiFolderPlus, FiPlay, FiPause, FiFileText, FiHelpCircle, FiDownload, FiChevronLeft, FiChevronRight, FiMoreVertical, FiCopy } from 'react-icons/fi';
import { formsAPI, foldersAPI, responsesAPI, templatesAPI } from '../services/api';
import ShareModal from '../components/ShareModal';
import FolderModal from '../components/FolderModal';
import FolderShareModal from '../components/FolderShareModal';
import FolderCard from '../components/FolderCard';
import CreateFormModal from '../components/CreateFormModal';
import UserShareModal from '../components/UserShareModal';
import TemplateSelectionModal from '../components/TemplateSelectionModal';
import AIGeneratorModal from '../components/AIGeneratorModal';
import { useAuth } from '../contexts/AuthContext';
import { isQuestionnaire } from '../utils/questionnaireScoring';

const DashboardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 20px;
  position: relative;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  padding: 24px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`;

const PageTitle = styled.h1`
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CreateFormButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: black;
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 12px;
  transition: all 0.2s ease;

  &:hover {
    background: #374151;
    text-decoration: none;
    color: white;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 32px;
`;

const StatCard = styled.div`
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  text-align: center;
  position: relative;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const StatValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
`;

const FormsSection = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  padding: 20px 24px 16px 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SectionTitle = styled.h2`
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FormsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  padding: 24px;
`;

const FormCard = styled.div`
  padding: 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-color: black;
  }
`;

const FormTitle = styled.h3`
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 6px 0;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const FormDescription = styled.p`
  font-size: 11px;
  color: #6b7280;
  margin: 0 0 10px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const FormMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  font-size: 11px;
  color: #9ca3af;

  span {
    padding: 2px 6px;
    background: #f3f4f6;
    border-radius: 4px;
    font-weight: 500;
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
`;

const FormDeactivatedBadge = styled.span`
  margin-left: 6px;
  font-size: 10px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #92400e;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  vertical-align: middle;
  flex-shrink: 0;
`;


const FormsListContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`;

const FormListItem = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    background: #f9fafb;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const FormListInfo = styled.div`
  flex: 1;
`;

const FormListTitle = styled.h3`
  font-size: 10px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 4px 0;
`;

const FormListMeta = styled.div`
  display: flex;
  gap: 16px;
  font-size: 10px;
  color: #6b7280;
`;

const FormListActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const FormListMenuHost = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const FormListMenuTrigger = styled.button`
  padding: 6px;
  background: none;
  color: #6b7280;
  border: none;
  border-radius: 6px;
  font-size: 18px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 0;

  &:hover {
    color: #1f2937;
    background: #f3f4f6;
  }
`;

const FormListMenuDropdown = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  min-width: 220px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.12);
  z-index: 200;
  padding: 4px 0;
`;

const FormListMenuItemBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  padding: 9px 14px;
  background: none;
  border: none;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #f3f4f6;
  }

  svg {
    flex-shrink: 0;
    opacity: 0.85;
  }
`;

const FormListMenuItemLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  padding: 9px 14px;
  text-decoration: none;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #f3f4f6;
    text-decoration: none;
    color: #374151;
  }

  svg {
    flex-shrink: 0;
    opacity: 0.85;
  }
`;

const FormListMenuItemDelete = styled(FormListMenuItemBtn)`
  color: #b91c1c;
  margin-top: 2px;
  border-top: 1px solid #e5e7eb;

  &:hover {
    background: #fef2f2;
    color: #991b1b;
  }
`;

const ActionButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px;
  background: none;
  color: #6b7280;
  text-decoration: none;
  border-radius: 6px;
  font-size: 16px;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;

  &:hover {
    text-decoration: none;
    color: #1f2937;
    background: #f3f4f6;
  }
`;

const ActionButtonStyled = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px;
  background: none;
  color: #6b7280;
  text-decoration: none;
  border-radius: 6px;
  font-size: 16px;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;

  &:hover {
    color: #1f2937;
    background: #f3f4f6;
  }
`;

const EmptyState = styled.div`
  padding: 60px 32px;
  text-align: center;
`;

const EmptyStateTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 10px 0;
`;

const EmptyStateDescription = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 0 0 20px 0;
  line-height: 1.6;
`;

// Pagination styled components
const Pagination = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PaginationInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`;

const PageInfo = styled.div`
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
`;

const PageSizeSelector = styled.select`
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #9ca3af;
  }

  &:focus {
    outline: none;
    border-color: #1f2937;
    box-shadow: 0 0 0 3px rgba(31, 41, 55, 0.1);
  }
`;

const PaginationButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: center;
`;

const PaginationButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: ${props => props.disabled ? '#f3f4f6' : 'white'};
  color: ${props => props.disabled ? '#9ca3af' : '#374151'};
  border: 1px solid ${props => props.disabled ? '#e5e7eb' : '#d1d5db'};
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #9ca3af;
    color: #1f2937;
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }
`;

const PageButton = styled.button`
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  background: ${props => props.active ? '#1f2937' : 'white'};
  color: ${props => props.active ? 'white' : '#374151'};
  border: 1px solid ${props => props.active ? '#1f2937' : '#d1d5db'};
  border-radius: 6px;
  font-size: 12px;
  font-weight: ${props => props.active ? '600' : '500'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.active ? '#374151' : '#f9fafb'};
    border-color: ${props => props.active ? '#374151' : '#9ca3af'};
  }
`;

/** API may return is_active as bit 0/1, boolean, or string */
const isFormDeactivated = (form) => {
  if (!form || form.is_active == null) return false;
  return !(form.is_active === 1 || form.is_active === true || String(form.is_active) === '1');
};

const Dashboard = () => {
  const { user } = useAuth();

  /** True when the logged-in user is an admin (matches backend checkIfAdmin / list views). Used to show full form actions on others' forms. */
  const isAdminUser = useMemo(() => {
    if (!user) return false;
    return (
      (user.role === 'admin' || user.role === 'Super Admin') ||
      (user.roles && user.roles.some((role) =>
        (typeof role === 'string' && (role === 'Super Admin' || role?.toLowerCase() === 'admin')) ||
        (typeof role === 'object' && role?.name && (role.name === 'Super Admin' || role.name?.toLowerCase() === 'admin'))
      )) ||
      (user.userRoles && user.userRoles.some((role) =>
        role === 'Super Admin' || role?.toLowerCase() === 'admin'
      )) ||
      (user.permissions && user.permissions.includes('admin')) ||
      user.isAdmin === true ||
      user.admin === true
    );
  }, [user]);
  
  // Debug user data
  useEffect(() => {
    console.log('🔍 Dashboard user data:', {
      user: user,
      userRoles: user?.roles,
      userPermissions: user?.permissions,
      userId: user?.id
    });
  }, [user]);
  const [loading, setLoading] = useState(true);
  const [forms, setForms] = useState([]);
  const [folders, setFolders] = useState([]);
  const [shareModal, setShareModal] = useState({ isOpen: false, form: null });
  const [folderModal, setFolderModal] = useState({ isOpen: false, folder: null, isEditing: false });
  const [moveToFolderModal, setMoveToFolderModal] = useState({ isOpen: false, form: null });
  const [folderShareModal, setFolderShareModal] = useState({ isOpen: false, folder: null });
  const [userShareModal, setUserShareModal] = useState({ isOpen: false, item: null, type: 'form' });
  const [createFormModal, setCreateFormModal] = useState(false);
  const [templateModal, setTemplateModal] = useState(false);
  const [aiGeneratorModal, setAiGeneratorModal] = useState({ isOpen: false, mode: 'text' });
  const [viewMode, setViewMode] = useState('surveys'); // 'folders', 'surveys', or 'questionnaires'
  const [selectedFolder, setSelectedFolder] = useState(null); // null means view all folders
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  /** Which form's row action menu is open in the main surveys/questionnaires list (id string / uuid) */
  const [formListMenuOpenId, setFormListMenuOpenId] = useState(null);
  const [stats, setStats] = useState({
    totalForms: 0,
    totalResponses: 0,
    activeForms: 0,
  });
  const [analytics, setAnalytics] = useState({
    totalViews: 0,
    totalSubmissions: 0,
    conversionRate: 0,
    recentActivity: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Reset to page 1 when viewMode changes
  useEffect(() => {
    setCurrentPage(1);
  }, [viewMode]);

  useEffect(() => {
    setFormListMenuOpenId(null);
  }, [viewMode, currentPage, pageSize]);

  useEffect(() => {
    if (formListMenuOpenId == null) return undefined;
    const onDocClick = (e) => {
      if (!e.target?.closest?.('[data-dashboard-form-menu]')) {
        setFormListMenuOpenId(null);
      }
    };
    const t = setTimeout(() => {
      document.addEventListener('click', onDocClick, true);
    }, 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('click', onDocClick, true);
    };
  }, [formListMenuOpenId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      console.log('🔄 Fetching dashboard data...');
      
      const [formsResponse, foldersResponse, analyticsResponse] = await Promise.all([
        formsAPI.getAll(),
        foldersAPI.getFolders(),
        responsesAPI.getAnalytics().catch(() => ({ data: { success: false } })) // Graceful fallback
      ]);
      
      console.log('📋 Forms API response:', formsResponse);
      console.log('📁 Folders API response:', foldersResponse);
      
      console.log('🔍 Forms received from backend:', formsResponse.data.data);
      setForms(formsResponse.data.data);
      setFolders(foldersResponse.data.data);
      
      // Calculate stats (excluding templates but including shared forms even if they are templates)
      const allForms = formsResponse.data.data;
      const nonTemplateForms = allForms.filter(form => !form.is_template);
      
      // Check if user is admin - more comprehensive check
      const isAdmin = (user?.role === 'admin' || user?.role === 'Super Admin') ||
                     (user?.roles && user.roles.some(role => 
                       (typeof role === 'string' && (role === 'Super Admin' || role?.toLowerCase() === 'admin')) ||
                       (typeof role === 'object' && role.name && (role.name === 'Super Admin' || role.name?.toLowerCase() === 'admin'))
                     )) ||
                     (user?.userRoles && user.userRoles.some(role => 
                       role === 'Super Admin' || role?.toLowerCase() === 'admin'
                     )) ||
                     // Additional checks for different possible structures
                     (user?.permissions && user.permissions.includes('admin')) ||
                     (user?.isAdmin === true) ||
                     (user?.admin === true) ||
                     // Check if user ID matches known admin IDs (fallback)
                     (user?.id === '9B2C4027-5FBC-493E-9273-C824AED4EEBA');
      
      console.log('🔍 Admin check debug:', {
        userId: user?.id,
        userRole: user?.role,
        userRoles: user?.roles,
        userUserRoles: user?.userRoles,
        isAdmin: isAdmin,
        environment: process.env.NODE_ENV,
        allUserKeys: Object.keys(user || {}),
        userStringified: JSON.stringify(user, null, 2)
      });
      
      // Filter forms that the user can access (owned or shared)
      // For admin users, show ALL forms. For regular users, show only owned + shared forms
      // Include shared forms even if they are templates
      const accessibleForms = allForms.filter(form => {
        if (isAdmin) {
          // Admin users can see all forms (including templates)
          console.log('🔍 Admin form access:', {
            formId: form.id,
            formTitle: form.title,
            accessible: true,
            reason: 'Admin user',
            isTemplate: form.is_template
          });
          return true;
        } else {
          // Regular users can see:
          // 1. Forms they own (even if templates)
          // 2. Forms shared with them (even if templates)
          // 3. Non-template forms they own
          const isOwner = form.created_by === user?.id;
          const hasSharedPermission = form.shared_permission;
          const isAccessible = isOwner || hasSharedPermission;
          
          console.log('🔍 Regular user form access:', {
            formId: form.id,
            formTitle: form.title,
            createdBy: form.created_by,
            currentUser: user?.id,
            isOwner: isOwner,
            hasSharedPermission: hasSharedPermission,
            isTemplate: form.is_template,
            accessible: isAccessible
          });
          
          // Debug logging for shared forms
          if (hasSharedPermission) {
            console.log('🔍 Shared form detected:', {
              formId: form.id,
              formTitle: form.title,
              isOwner: isOwner,
              hasSharedPermission: hasSharedPermission,
              sharedPermissionValue: form.shared_permission,
              sharedPermissionType: typeof form.shared_permission,
              isTemplate: form.is_template,
              isAccessible: isAccessible
            });
          }
          
          return isAccessible;
        }
      });
      
      const totalForms = accessibleForms.length;
      const activeForms = accessibleForms.filter(form => form.is_active).length;
      
      // Debug each accessible form to see its type
      console.log('🔍 Accessible forms type analysis:', accessibleForms.map(form => {
        const isQ = isQuestionnaire(form);
        const isS = !isQ;
        return {
          id: form.id,
          title: form.title,
          isQuestionnaire: isQ,
          isSurvey: isS,
          form_structure: form.form_structure,
          form_structure_type: typeof form.form_structure,
          isQuestionnaire_result: isQ
        };
      }));
      
      const surveys = accessibleForms.filter(form => !isQuestionnaire(form));
      const questionnaires = accessibleForms.filter(form => isQuestionnaire(form));
      
      console.log('🔍 Stats calculation debug:', {
        allForms: allForms.length,
        nonTemplateForms: nonTemplateForms.length,
        accessibleForms: accessibleForms.length,
        surveys: surveys.length,
        questionnaires: questionnaires.length,
        user: user?.id,
        isAdmin: isAdmin,
        userObject: user
      });
      
      console.log('🔍 Survey vs Questionnaire breakdown:', {
        surveys: surveys.map(s => ({ id: s.id, title: s.title, isQuestionnaire: isQuestionnaire(s) })),
        questionnaires: questionnaires.map(q => ({ id: q.id, title: q.title, isQuestionnaire: isQuestionnaire(q) }))
      });
      
      // Debug: Show all non-template forms and their permission status
      console.log('🔍 All non-template forms with permissions:', nonTemplateForms.map(form => ({
        id: form.id,
        title: form.title,
        created_by: form.created_by,
        shared_permission: form.shared_permission,
        isOwner: form.created_by === user?.id,
        isAccessible: form.created_by === user?.id || form.shared_permission
      })));
      
      setStats({
        totalForms,
        totalResponses: accessibleForms.reduce((sum, form) => sum + (form.response_count || 0), 0),
        activeForms,
        totalSurveys: surveys.length,
        totalQuestionnaires: questionnaires.length,
      });

      // Set analytics data
      if (analyticsResponse.data.success) {
        setAnalytics({
          totalViews: analyticsResponse.data.data.totalViews || 0,
          totalSubmissions: analyticsResponse.data.data.totalSubmissions || 0,
          conversionRate: analyticsResponse.data.data.conversionRate || 0,
          recentActivity: analyticsResponse.data.data.recentActivity || []
        });
      }
    } catch (error) {
      console.error('❌ Dashboard data error:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      toast.error('Failed to load dashboard data: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteForm = async (formId, formTitle) => {
    if (window.confirm(`Are you sure you want to delete "${formTitle}"?`)) {
      try {
        await formsAPI.delete(formId);
        // Refresh the forms list from server to ensure deleted forms are removed
        await fetchData();
        toast.success('Form deleted successfully');
      } catch (error) {
        toast.error('Failed to delete form');
        console.error('Delete form error:', error);
      }
    }
  };

  const handleDuplicateForm = async (form) => {
    try {
      const res = await formsAPI.duplicate(form.id, {});
      if (res.data?.success && res.data?.data?.id) {
        toast.success(`Duplicated as "${res.data.data.title}"`);
        await fetchData();
        const newId = res.data.data.id;
        if (isQuestionnaire(form)) {
          window.location.href = `/questionnaire-builder/${newId}`;
        } else {
          window.location.href = `/builder/${newId}`;
        }
      } else {
        toast.error('Duplicate failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to duplicate form');
      console.error('Duplicate form error:', error);
    }
  };

  const handleMoveToFolder = async (formId, folderId) => {
    try {
      await formsAPI.update(formId, { folder_id: folderId });
      toast.success('Form moved to folder successfully');
      fetchData();
      setMoveToFolderModal({ isOpen: false, form: null });
    } catch (error) {
      toast.error('Failed to move form to folder');
      console.error('Move to folder error:', error);
    }
  };

  const openMoveToFolderModal = (form) => {
    setMoveToFolderModal({ isOpen: true, form });
  };

  const openFolderShareModal = (folder) => {
    setFolderShareModal({ isOpen: true, folder });
  };

  const openFolderUserShareModal = (folder) => {
    setUserShareModal({ isOpen: true, item: folder, type: 'folder' });
  };

  const closeUserShareModal = () => {
    setUserShareModal({ isOpen: false, item: null, type: 'form' });
  };

  const handleToggleFormStatus = async (formId, isActive) => {
    try {
      const updateData = { is_active: isActive };
      await formsAPI.update(formId, updateData);
      
      // Update the form in the local state
      setForms(forms.map(form => 
        form.id === formId ? { ...form, is_active: isActive } : form
      ));
      
      const statusText = isActive ? 'activated' : 'deactivated';
      toast.success(`Form ${statusText} successfully!`);
    } catch (error) {
      toast.error('Failed to update form status');
      console.error('Toggle form status error:', error);
    }
  };

  const openShareModal = (form) => {
    setShareModal({ isOpen: true, form });
  };

  const closeShareModal = () => {
    setShareModal({ isOpen: false, form: null });
  };

  const handleCreateFolder = async (folderData) => {
    try {
      const response = await foldersAPI.createFolder(folderData);
      setFolders([...folders, response.data.data]);
      toast.success('Folder created successfully');
      closeFolderModal();
    } catch (error) {
      toast.error('Failed to create folder');
      console.error('Create folder error:', error);
    }
  };

  const handleUpdateFolder = async (folderData) => {
    try {
      const response = await foldersAPI.updateFolder(folderModal.folder.id, folderData);
      setFolders(folders.map(folder => 
        folder.id === folderModal.folder.id ? response.data.data : folder
      ));
      toast.success('Folder updated successfully');
      closeFolderModal();
    } catch (error) {
      toast.error('Failed to update folder');
      console.error('Update folder error:', error);
    }
  };

  const handleDeleteFolder = async (folderId) => {
    try {
      await foldersAPI.deleteFolder(folderId);
      setFolders(folders.filter(folder => folder.id !== folderId));
      toast.success('Folder deleted successfully');
      closeFolderModal();
    } catch (error) {
      toast.error('Failed to delete folder');
      console.error('Delete folder error:', error);
    }
  };

  const openFolderModal = (folder = null, isEditing = false) => {
    setFolderModal({ isOpen: true, folder, isEditing });
  };

  const closeFolderModal = () => {
    setFolderModal({ isOpen: false, folder: null, isEditing: false });
  };

  const handleViewFolder = async (folder) => {
    try {
      setLoading(true);
      const response = await foldersAPI.getFolder(folder.id);
      setSelectedFolder(response.data.data);
      setViewMode('surveys'); // Switch to surveys view to show folder contents
    } catch (error) {
      toast.error('Failed to load folder contents');
      console.error('Load folder error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToFolders = () => {
    setSelectedFolder(null);
    setViewMode('folders');
  };

  const handleFormClick = (form) => {
    // Check if user is admin (check both role property and roles array)
    const isAdmin = (user?.role === 'admin' || user?.role === 'Super Admin') ||
                   (user?.roles && user.roles.some(role => 
                     role.name === 'Super Admin' || role.name?.toLowerCase() === 'admin'
                   )) ||
                   (user?.userRoles && user.userRoles.some(role => 
                     role === 'Super Admin' || role?.toLowerCase() === 'admin'
                   ));
    
    // Check if user has access to view responses (full access or view_responses permission)
    const hasResponseAccess = form.shared_permission === 'full' || form.shared_permission === 'view_responses';
    
    console.log('🔍 Form click debug:', {
      formId: form.id,
      formTitle: form.title,
      formOwner: form.created_by,
      currentUser: user?.id,
      userRoles: user?.roles,
      isAdmin: isAdmin,
      isOwner: form.created_by === user?.id,
      sharedPermission: form.shared_permission,
      sharedPermissionType: typeof form.shared_permission,
      hasResponseAccess: hasResponseAccess,
      completeFormObject: form
    });
    
    // Navigate to responses page if user is owner, admin, or has response access
    if (form.created_by === user?.id || isAdmin || hasResponseAccess) {
      console.log('🔍 Redirecting to responses page (owner, admin, or response access)');
      window.location.href = `/responses/${form.id}`;
    } else {
      console.log('🔍 Redirecting to form view (view only or no permission)');
      console.log('🔍 Permission check details:', {
        isOwner: form.created_by === user?.id,
        isAdmin: isAdmin,
        hasResponseAccess: hasResponseAccess,
        sharedPermission: form.shared_permission,
        shouldRedirectToResponses: form.created_by === user?.id || isAdmin || hasResponseAccess
      });
      // Navigate to form view for users with view-only or no specific permissions
      window.open(`/form/${form.id}`, '_blank');
    }
  };

  const handleViewForm = (form) => {
    // Check if user is admin (check both role property and roles array)
    const isAdmin = (user?.role === 'admin' || user?.role === 'Super Admin') ||
                   (user?.roles && user.roles.some(role => 
                     role.name === 'Super Admin' || role.name?.toLowerCase() === 'admin'
                   )) ||
                   (user?.userRoles && user.userRoles.some(role => 
                     role === 'Super Admin' || role?.toLowerCase() === 'admin'
                   ));
    
    // Check if user has full access permission for this shared form
    const hasFullAccess = form.shared_permission === 'full';
    
    // For shared forms (not owned by current user), use the shared form endpoint
    if (form.created_by !== user?.id && !isAdmin && !hasFullAccess) {
      // Use the new shared form endpoint
      window.open(`/form/${form.id}`, '_blank');
    } else {
      // For user's own forms, admin, or users with full access, use the regular form view
      window.location.href = `/form/${form.id}`;
    }
  };

  const handleCreateFormOption = (option) => {
    switch (option) {
      case 'scratch':
        // Navigate to form builder for creating from scratch
        window.location.href = '/builder';
        break;
      case 'template':
        // Show template selection modal
        setTemplateModal(true);
        break;
      case 'ai':
        // Close the create form modal first
        setCreateFormModal(false);
        // Show AI generator modal in text mode
        setAiGeneratorModal({ isOpen: true, mode: 'text' });
        break;
      case 'upload':
        // Close the create form modal first
        setCreateFormModal(false);
        // Show AI generator modal in file upload mode
        setAiGeneratorModal({ isOpen: true, mode: 'file' });
        break;
      case 'questionnaire':
        // Navigate to questionnaire builder
        window.location.href = '/questionnaire-builder';
        break;
      default:
        break;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const handleTemplateSelection = async (template) => {
    try {
      // Create a form from the template using the API
      const response = await templatesAPI.useTemplate(template.id, {
      title: template.title,
        description: template.description
      });
      
      if (response.data.success) {
        toast.success('Form created from template successfully');
        // Refresh the forms list
        await fetchData();
        // Close the template modal
        setTemplateModal(false);
        // Navigate to the form builder to edit the newly created form
        window.location.href = `/builder/${response.data.data.id}`;
      }
    } catch (error) {
      console.error('Error creating form from template:', error);
      toast.error('Failed to create form from template');
    }
  };

  const handleAIGeneration = (formStructure, prompt) => {
    console.log('handleAIGeneration called with:', formStructure);
    console.log('formStructure type:', typeof formStructure);
    console.log('formStructure is array:', Array.isArray(formStructure));
    
    // Handle both cases: formStructure as array or as object with form_structure property
    const fields = Array.isArray(formStructure) ? formStructure : (formStructure.form_structure || []);
    const title = formStructure.title || `AI Generated Form - ${new Date().toLocaleDateString()}`;
    const description = formStructure.description || `Generated from: "${prompt}"`;
    
    console.log('Fields count:', fields.length);
    console.log('Title:', title);
    console.log('Description:', description);
    
    // Navigate to form builder with AI-generated form data
    localStorage.setItem('aiGeneratedForm', JSON.stringify({
      title: title,
      description: description,
      form_structure: fields,
      logo_url: formStructure.logo_url || '',
      isAIGenerated: true
    }));
    window.location.href = '/builder?fromAI=true';
  };

  const isFormExpired = (form) => {
    if (!form.expiry_date) return false;
    return new Date() > new Date(form.expiry_date);
  };

  const getFormStatus = (form) => {
    // Check if form is manually deactivated
    if (!form.is_active) {
      return { status: 'inactive', label: 'Deactivated', color: '#b45309' };
    }
    
    // Check if form has expired
    if (isFormExpired(form)) {
      return { status: 'expired', label: 'Expired', color: '#dc2626' };
    }
    
    // Form is active and not expired
    return { status: 'active', label: 'Active', color: '#059669' };
  };

  const handleDownloadWord = async (form) => {
    try {
      const res = await formsAPI.exportWord(form.id);
      const blob = new Blob([res.data], { type: 'application/msword' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${form.title || 'Form'}.doc`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Word document downloaded successfully');
    } catch (err) {
      console.error('Word export failed:', err);
      toast.error('Failed to download Word file');
    }
  };


  if (loading) {
    return (
      <DashboardContainer>
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <div style={{ fontSize: '14px', color: '#6b7280' }}>Loading dashboard...</div>
        </div>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <PageHeader>
        <PageTitle>
          📊 Dashboard
        </PageTitle>
        {user?.permissions?.includes('form.create') && (
          <CreateFormButton as="button" onClick={() => setCreateFormModal(true)}>
            <FiPlus />
            Create New Form
          </CreateFormButton>
        )}
      </PageHeader>

      {/* Dashboard Stats */}
      <StatsGrid>
        <StatCard>
          <StatValue>{stats.totalSurveys}</StatValue>
          <StatLabel>📝 Surveys</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.totalQuestionnaires}</StatValue>
          <StatLabel>🎯 Questionnaires</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.activeForms}</StatValue>
          <StatLabel>Active Forms</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{analytics.conversionRate.toFixed(1)}%</StatValue>
          <StatLabel>Conversion Rate</StatLabel>
        </StatCard>
      </StatsGrid>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px',
        padding: '20px 32px',
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.2)'
      }}>
        <div style={{ display: 'flex', gap: '12px' }}>
          {selectedFolder ? (
            <button
              onClick={handleBackToFolders}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.3s ease'
              }}
            >
              ← Back to Folders
            </button>
          ) : (
            <>
              <button
                onClick={() => {
                  setViewMode('folders');
                  setSelectedFolder(null);
                }}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '12px',
                  background: viewMode === 'folders' 
                    ? 'linear-gradient(135deg, black 0%, black 100%)' 
                    : 'rgba(255, 255, 255, 0.8)',
                  color: viewMode === 'folders' ? 'white' : '#6b7280',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease'
                }}
              >
                <FiFolder />
                Folders ({folders.length})
              </button>
              <button
                onClick={() => {
                  setViewMode('surveys');
                  setSelectedFolder(null);
                }}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '12px',
                  background: viewMode === 'surveys' 
                    ? 'linear-gradient(135deg, black 0%, black 100%)' 
                    : 'rgba(255, 255, 255, 0.8)',
                  color: viewMode === 'surveys' ? 'white' : '#6b7280',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease'
                }}
              >
                <FiFileText />
                Surveys ({stats.totalSurveys})
              </button>
              <button
                onClick={() => {
                  setViewMode('questionnaires');
                  setSelectedFolder(null);
                }}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '12px',
                  background: viewMode === 'questionnaires' 
                    ? 'linear-gradient(135deg, black 0%, black 100%)' 
                    : 'rgba(255, 255, 255, 0.8)',
                  color: viewMode === 'questionnaires' ? 'white' : '#6b7280',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease'
                }}
              >
                <FiHelpCircle />
                Questionnaires ({stats.totalQuestionnaires})
              </button>
            </>
          )}
        </div>
        
        {viewMode === 'folders' && user?.permissions?.includes('form.create') && (
          <button
            onClick={() => openFolderModal()}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.3s ease'
            }}
          >
            <FiFolderPlus />
            New Folder
          </button>
        )}
      </div>
      {selectedFolder ? (
        // Show forms in selected folder
        <FormsSection>
          <SectionHeader>
            <SectionTitle>
              📁 {selectedFolder.folder.name} ({selectedFolder.forms.length} forms)
            </SectionTitle>
          </SectionHeader>
          
          {selectedFolder.forms.length === 0 ? (
            <EmptyState>
              <EmptyStateTitle>No forms in this folder</EmptyStateTitle>
              <EmptyStateDescription>
                Forms you assign to this folder will appear here
              </EmptyStateDescription>
            </EmptyState>
          ) : (
            <FormsGrid>
              {selectedFolder.forms.map((form) => (
                <FormCard key={form.id}>
                  <FormTitle>
                    {form.title}
                    <span style={{
                      marginLeft: '8px',
                      fontSize: '11px',
                      padding: '3px 10px',
                      borderRadius: '12px',
                      background: isQuestionnaire(form) ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: 'white',
                      fontWeight: '600'
                    }}>
                      {isQuestionnaire(form) ? '🎯 Quiz' : '📝 Survey'}
                    </span>
                    {isFormDeactivated(form) && (
                      <FormDeactivatedBadge title="This form is turned off and not accepting responses">
                        Deactivated
                      </FormDeactivatedBadge>
                    )}
                  </FormTitle>
                  <FormDescription>
                    {form.description || 'No description provided'}
                  </FormDescription>
                  <FormMeta>
                    <span>Created {formatDate(form.created_at)}</span>
                    <span style={{ color: getFormStatus(form).color }}>
                      {getFormStatus(form).label}
                    </span>
                  </FormMeta>
                  <FormActions>
                    <ActionButtonStyled
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewForm(form);
                      }}
                      title="View Form"
                    >
                      <FiEye />
                    </ActionButtonStyled>
                    {/* Show management actions for owners, full/edit share, or admins (API gives admins shared_permission null) */}
                    {(isAdminUser || form.created_by === user?.id || form.shared_permission === 'full' || form.shared_permission === 'edit') && (
                      <>
                        <ActionButtonStyled
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadWord(form);
                          }}
                          title="Download as Word"
                        >
                          <FiDownload />
                        </ActionButtonStyled>
                        {user?.permissions?.includes('form.create') && (
                          <ActionButtonStyled
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDuplicateForm(form);
                            }}
                            title="Duplicate form"
                          >
                            <FiCopy />
                          </ActionButtonStyled>
                        )}
                    {/* Show share options for owners, full access, or admins */}
                    {(isAdminUser || form.created_by === user?.id || form.shared_permission === 'full') && (
                          <>
                            <ActionButtonStyled
                          onClick={(e) => {
                            e.stopPropagation();
                            openShareModal(form);
                          }}
                          title="Share Publicly"
                        >
                          <FiShare2 />
                        </ActionButtonStyled>
                        <ActionButtonStyled
                          onClick={(e) => {
                            e.stopPropagation();
                            setUserShareModal({ isOpen: true, item: form, type: 'form' });
                          }}
                          title="Share with Users"
                        >
                          <FiUsers />
                        </ActionButtonStyled>
                          </>
                        )}
                        {/* Owners and admins can activate/deactivate */}
                        {(isAdminUser || form.created_by === user?.id) && (
                        <ActionButtonStyled
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleFormStatus(form.id, !form.is_active);
                          }}
                          title={form.is_active ? 'Deactivate Form' : 'Activate Form'}
                        >
                          {form.is_active ? <FiPause /> : <FiPlay />}
                        </ActionButtonStyled>
                        )}
                        <ActionButton 
                          to={isQuestionnaire(form) ? `/questionnaire-builder/${form.id}` : `/builder/${form.id}`}
                          title="Edit Form"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <FiEdit />
                        </ActionButton>
                        {(isAdminUser || user?.permissions?.includes('response.read') || form.shared_permission === 'full' || form.shared_permission === 'view_responses') && (
                          <ActionButton 
                            to={`/responses/${form.id}`} 
                            title="View Responses"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent event bubbling to parent
                              console.log('🔍 View Responses clicked for form:', {
                                formId: form.id,
                                formTitle: form.title,
                                targetUrl: `/responses/${form.id}`
                              });
                            }}
                          >
                            <FiBarChart2 />
                          </ActionButton>
                        )}
                        {/* Owners and admins can delete */}
                        {(isAdminUser || form.created_by === user?.id) && (
                        <ActionButtonStyled
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteForm(form.id, form.title);
                          }}
                          title="Delete Form"
                        >
                          <FiTrash2 />
                        </ActionButtonStyled>
                        )}
                      </>
                    )}
                    {/* Show a badge for forms from shared folders */}
                    {form.created_by !== user?.id && form.folder_name && (
                      <span style={{
                        fontSize: '10px',
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontWeight: '500',
                        marginLeft: '8px'
                      }}>
                        Shared
                      </span>
                    )}
                    {isAdminUser && form.created_by !== user?.id && (
                      <span style={{
                        fontSize: '10px',
                        background: 'linear-gradient(135deg, #0f172a 0%, #334155 100%)',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontWeight: '500',
                        marginLeft: '8px'
                      }}>
                        Admin
                      </span>
                    )}
                  </FormActions>
                </FormCard>
              ))}
            </FormsGrid>
          )}
        </FormsSection>
      ) : viewMode === 'folders' ? (
        // Show folders view
        <FormsSection>
          <SectionHeader>
            <SectionTitle>Your Folders</SectionTitle>
          </SectionHeader>
          
          {folders.length === 0 ? (
            <EmptyState>
              <EmptyStateTitle>No folders yet</EmptyStateTitle>
              <EmptyStateDescription>
                Create folders to organize your forms better
              </EmptyStateDescription>
              {user?.permissions?.includes('form.create') && (
                <CreateFormButton onClick={() => openFolderModal()}>
                  <FiFolderPlus />
                  Create Your First Folder
                </CreateFormButton>
              )}
            </EmptyState>
          ) : (
            <FormsGrid>
              {folders.map((folder) => (
                <FolderCard
                  key={folder.id}
                  folder={folder}
                  onView={handleViewFolder}
                  onEdit={folder.folder_type === 'owned' ? (folder) => openFolderModal(folder, true) : null}
                  onDelete={folder.folder_type === 'owned' ? (folder) => handleDeleteFolder(folder.id) : null}
                  onShare={folder.folder_type === 'owned' ? (folder) => openFolderShareModal(folder) : null}
                  onShareWithUsers={folder.folder_type === 'owned' ? (folder) => openFolderUserShareModal(folder) : null}
                  onClick={() => handleViewFolder(folder)}
                />
              ))}
            </FormsGrid>
          )}
        </FormsSection>
      ) : (
        // Show surveys or questionnaires view
        <FormsListContainer>
          <SectionHeader>
            <SectionTitle>
              {viewMode === 'surveys' ? '📝 Surveys' : '🎯 Questionnaires'}
            </SectionTitle>
          </SectionHeader>
        
          {(() => {
            console.log('🔍 Dashboard filtering debug:', {
              viewMode: viewMode,
              totalForms: forms.length,
              forms: forms.map(f => ({
                id: f.id,
                title: f.title,
                created_by: f.created_by,
                is_template: f.is_template,
                shared_permission: f.shared_permission,
                isQuestionnaire: isQuestionnaire(f)
              }))
            });
            
            // Use the same comprehensive admin logic as stats calculation
            const isAdminForUI = (user?.role === 'admin' || user?.role === 'Super Admin') ||
                               (user?.roles && user.roles.some(role => 
                                 (typeof role === 'string' && (role === 'Super Admin' || role?.toLowerCase() === 'admin')) ||
                                 (typeof role === 'object' && role.name && (role.name === 'Super Admin' || role.name?.toLowerCase() === 'admin'))
                               )) ||
                               (user?.userRoles && user.userRoles.some(role => 
                                 role === 'Super Admin' || role?.toLowerCase() === 'admin'
                               )) ||
                               // Additional checks for different possible structures
                               (user?.permissions && user.permissions.includes('admin')) ||
                               (user?.isAdmin === true) ||
                               (user?.admin === true) ||
                               // Check if user ID matches known admin IDs (fallback)
                               (user?.id === '9B2C4027-5FBC-493E-9273-C824AED4EEBA');
            
            const filteredForms = viewMode === 'surveys' 
              ? forms.filter(form => {
                  if (isAdminForUI) {
                    // Admin users can see all surveys (including shared templates)
                    return !isQuestionnaire(form);
                  } else {
                    // Regular users can see surveys they own or that are shared with them (including templates)
                    const isOwner = form.created_by === user?.id;
                    const hasSharedPermission = form.shared_permission;
                    const shouldInclude = !isQuestionnaire(form) && (isOwner || hasSharedPermission);
                    
                    if (form.id === '9A9E2F93-9A8B-438B-9DF5-CBE34AEEE2B6') {
                      console.log('🔍 Filtering debug for R&D BRW JON:', {
                        formId: form.id,
                        formTitle: form.title,
                        isQuestionnaire: isQuestionnaire(form),
                        isOwner: isOwner,
                        hasSharedPermission: hasSharedPermission,
                        sharedPermissionValue: form.shared_permission,
                        isTemplate: form.is_template,
                        shouldInclude: shouldInclude,
                        formCreatedBy: form.created_by,
                        currentUser: user?.id
                      });
                    }
                    
                    return shouldInclude;
                  }
                })
              : forms.filter(form => {
                  if (isAdminForUI) {
                    // Admin users can see all questionnaires (including shared templates)
                    return isQuestionnaire(form);
                  } else {
                    // Regular users can see questionnaires they own or that are shared with them (including templates)
                    const isOwner = form.created_by === user?.id;
                    const hasSharedPermission = form.shared_permission;
                    const shouldInclude = isQuestionnaire(form) && (isOwner || hasSharedPermission);
                    
                    if (form.id === '9A9E2F93-9A8B-438B-9DF5-CBE34AEEE2B6') {
                      console.log('🔍 Filtering debug for R&D BRW JON (questionnaire):', {
                        formId: form.id,
                        formTitle: form.title,
                        isQuestionnaire: isQuestionnaire(form),
                        isOwner: isOwner,
                        hasSharedPermission: hasSharedPermission,
                        sharedPermissionValue: form.shared_permission,
                        isTemplate: form.is_template,
                        shouldInclude: shouldInclude,
                        formCreatedBy: form.created_by,
                        currentUser: user?.id
                      });
                    }
                    
                    return shouldInclude;
                  }
                });
            
            console.log('🔍 Filtered forms:', {
              viewMode: viewMode,
              filteredCount: filteredForms.length,
              filteredForms: filteredForms.map(f => ({
                id: f.id,
                title: f.title,
                created_by: f.created_by,
                shared_permission: f.shared_permission
              }))
            });
            
            // Calculate pagination
            const totalForms = filteredForms.length;
            const totalPages = Math.ceil(totalForms / pageSize);
            const startIndex = (currentPage - 1) * pageSize;
            const endIndex = startIndex + pageSize;
            const paginatedForms = filteredForms.slice(startIndex, endIndex);
            
            return filteredForms.length === 0 ? (
            <EmptyState>
              <EmptyStateTitle>
                No {viewMode === 'surveys' ? 'surveys' : 'questionnaires'} yet
              </EmptyStateTitle>
              <EmptyStateDescription>
                Create your first {viewMode === 'surveys' ? 'survey' : 'questionnaire'} to get started
              </EmptyStateDescription>
              {user?.permissions?.includes('form.create') && (
                <CreateFormButton as="button" onClick={() => setCreateFormModal(true)}>
                  <FiPlus />
                  Create Your First {viewMode === 'surveys' ? 'Survey' : 'Questionnaire'}
                </CreateFormButton>
              )}
            </EmptyState>
          ) : (
            <>
            <div>
                {paginatedForms.map((form) => {
                const canManage = isAdminUser || form.created_by === user?.id || form.shared_permission === 'full' || form.shared_permission === 'edit';
                const canShareForm = isAdminUser || form.created_by === user?.id || form.shared_permission === 'full';
                const canViewResp = isAdminUser || user?.permissions?.includes('response.read') || form.shared_permission === 'full' || form.shared_permission === 'view_responses';
                const isOwnerOrAdmin = isAdminUser || form.created_by === user?.id;
                return (
                <FormListItem
                  key={form.id}
                  onClick={() => handleFormClick(form)}
                  style={formListMenuOpenId === form.id ? { position: 'relative', zIndex: 30 } : undefined}
                >
                  <FormListInfo>
                    <FormListTitle>
                      {form.title}
                      <span style={{
                        marginLeft: '8px',
                        fontSize: '11px',
                        padding: '3px 10px',
                        borderRadius: '12px',
                        background: isQuestionnaire(form) ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        fontWeight: '600'
                      }}>
                        {isQuestionnaire(form) ? '🎯 Quiz' : '📝 Survey'}
                      </span>
                      {isFormDeactivated(form) && (
                        <FormDeactivatedBadge title="This form is turned off and not accepting responses">
                          Deactivated
                        </FormDeactivatedBadge>
                      )}
                    </FormListTitle>
                    <FormListMeta>
                      <span>Created {formatDate(form.created_at)}</span>
                      <span style={{ color: getFormStatus(form).color }}>
                        {getFormStatus(form).label}
                      </span>
                      <span>{form.response_count || 0} responses</span>
                      {form.folder_name && (
                        <span style={{ color: form.folder_color || '#6b7280' }}>
                          📁 {form.folder_name}
                        </span>
                      )}
                    </FormListMeta>
                  </FormListInfo>
                  <FormListActions onClick={(e) => e.stopPropagation()}>
                    <FormListMenuHost data-dashboard-form-menu>
                      <FormListMenuTrigger
                        type="button"
                        title="Actions"
                        aria-label="Form actions"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormListMenuOpenId((p) => (p === form.id ? null : form.id));
                        }}
                      >
                        <FiMoreVertical />
                      </FormListMenuTrigger>
                      {formListMenuOpenId === form.id && (
                        <FormListMenuDropdown data-dashboard-form-menu>
                          <FormListMenuItemBtn
                            type="button"
                            onClick={() => {
                              handleViewForm(form);
                              setFormListMenuOpenId(null);
                            }}
                          >
                            <FiEye size={16} />
                            View form
                          </FormListMenuItemBtn>
                          {canManage && (
                            <>
                              <FormListMenuItemLink
                                to={isQuestionnaire(form) ? `/questionnaire-builder/${form.id}` : `/builder/${form.id}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFormListMenuOpenId(null);
                                }}
                              >
                                <FiEdit size={16} />
                                Edit
                              </FormListMenuItemLink>
                              {user?.permissions?.includes('form.create') && (
                                <FormListMenuItemBtn
                                  type="button"
                                  onClick={() => {
                                    handleDuplicateForm(form);
                                    setFormListMenuOpenId(null);
                                  }}
                                >
                                  <FiCopy size={16} />
                                  Duplicate
                                </FormListMenuItemBtn>
                              )}
                              {canViewResp && (
                                <FormListMenuItemLink
                                  to={`/responses/${form.id}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFormListMenuOpenId(null);
                                  }}
                                >
                                  <FiBarChart2 size={16} />
                                  View responses
                                </FormListMenuItemLink>
                              )}
                              <FormListMenuItemBtn
                                type="button"
                                onClick={() => {
                                  handleDownloadWord(form);
                                  setFormListMenuOpenId(null);
                                }}
                              >
                                <FiDownload size={16} />
                                Download as Word
                              </FormListMenuItemBtn>
                              {canShareForm && (
                                <>
                                  <FormListMenuItemBtn
                                    type="button"
                                    onClick={() => {
                                      openShareModal(form);
                                      setFormListMenuOpenId(null);
                                    }}
                                  >
                                    <FiShare2 size={16} />
                                    Share publicly
                                  </FormListMenuItemBtn>
                                  <FormListMenuItemBtn
                                    type="button"
                                    onClick={() => {
                                      setUserShareModal({ isOpen: true, item: form, type: 'form' });
                                      setFormListMenuOpenId(null);
                                    }}
                                  >
                                    <FiUsers size={16} />
                                    Share with users
                                  </FormListMenuItemBtn>
                                </>
                              )}
                              {isOwnerOrAdmin && (
                                <>
                                  <FormListMenuItemBtn
                                    type="button"
                                    onClick={() => {
                                      openMoveToFolderModal(form);
                                      setFormListMenuOpenId(null);
                                    }}
                                  >
                                    <FiFolder size={16} />
                                    Move to folder
                                  </FormListMenuItemBtn>
                                  <FormListMenuItemBtn
                                    type="button"
                                    onClick={() => {
                                      handleToggleFormStatus(form.id, !form.is_active);
                                      setFormListMenuOpenId(null);
                                    }}
                                  >
                                    {form.is_active ? <FiPause size={16} /> : <FiPlay size={16} />}
                                    {form.is_active ? 'Deactivate' : 'Activate'}
                                  </FormListMenuItemBtn>
                                  <FormListMenuItemDelete
                                    type="button"
                                    onClick={() => {
                                      handleDeleteForm(form.id, form.title);
                                      setFormListMenuOpenId(null);
                                    }}
                                  >
                                    <FiTrash2 size={16} />
                                    Delete form
                                  </FormListMenuItemDelete>
                                </>
                              )}
                            </>
                          )}
                        </FormListMenuDropdown>
                      )}
                    </FormListMenuHost>
                    {/* Show a badge for forms from shared folders */}
                    {form.created_by !== user?.id && form.folder_name && (
                      <span style={{
                        fontSize: '10px',
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontWeight: '500',
                        marginLeft: '8px'
                      }}>
                        Shared
                      </span>
                    )}
                    {isAdminUser && form.created_by !== user?.id && (
                      <span style={{
                        fontSize: '10px',
                        background: 'linear-gradient(135deg, #0f172a 0%, #334155 100%)',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontWeight: '500',
                        marginLeft: '8px'
                      }}>
                        Admin
                      </span>
                    )}
                  </FormListActions>
                </FormListItem>
              );})}
            </div>
              
              {/* Pagination Controls */}
              {totalForms > 0 && (
                <Pagination>
                  <PaginationInfo>
                    <PageInfo>
                      Showing {startIndex + 1} to {Math.min(endIndex, totalForms)} of {totalForms} {viewMode === 'surveys' ? 'surveys' : 'questionnaires'}
                    </PageInfo>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Per page:</label>
                      <PageSizeSelector
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                          setCurrentPage(1); // Reset to first page when changing page size
                        }}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </PageSizeSelector>
                    </div>
                  </PaginationInfo>
                  
                  {totalPages > 1 && (
                    <PaginationButtons>
                      <PaginationButton
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        title="First page"
                      >
                        First
                      </PaginationButton>
                      
                      <PaginationButton
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        title="Previous page"
                      >
                        <FiChevronLeft />
                        Previous
                      </PaginationButton>
                      
                      {/* Page number buttons */}
                      {(() => {
                        const pages = [];
                        const maxVisiblePages = 5;
                        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                        
                        // Adjust start page if we're near the end
                        if (endPage - startPage < maxVisiblePages - 1) {
                          startPage = Math.max(1, endPage - maxVisiblePages + 1);
                        }
                        
                        // First page
                        if (startPage > 1) {
                          pages.push(
                            <PageButton
                              key={1}
                              onClick={() => setCurrentPage(1)}
                              active={currentPage === 1}
                            >
                              1
                            </PageButton>
                          );
                          if (startPage > 2) {
                            pages.push(
                              <span key="ellipsis1" style={{ padding: '0 4px', color: '#9ca3af', fontSize: '12px' }}>...</span>
                            );
                          }
                        }
                        
                        // Visible page range
                        for (let i = startPage; i <= endPage; i++) {
                          pages.push(
                            <PageButton
                              key={i}
                              onClick={() => setCurrentPage(i)}
                              active={currentPage === i}
                            >
                              {i}
                            </PageButton>
                          );
                        }
                        
                        // Last page
                        if (endPage < totalPages) {
                          if (endPage < totalPages - 1) {
                            pages.push(
                              <span key="ellipsis2" style={{ padding: '0 4px', color: '#9ca3af', fontSize: '12px' }}>...</span>
                            );
                          }
                          pages.push(
                            <PageButton
                              key={totalPages}
                              onClick={() => setCurrentPage(totalPages)}
                              active={currentPage === totalPages}
                            >
                              {totalPages}
                            </PageButton>
                          );
                        }
                        
                        return pages;
                      })()}
                      
                      <PaginationButton
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage >= totalPages}
                        title="Next page"
                      >
                        Next
                        <FiChevronRight />
                      </PaginationButton>
                      
                      <PaginationButton
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage >= totalPages}
                        title="Last page"
                      >
                        Last
                      </PaginationButton>
                    </PaginationButtons>
                  )}
                </Pagination>
              )}
            </>
          );
          })()}
        </FormsListContainer>
      )}

      <FolderModal
        isOpen={folderModal.isOpen}
        onClose={closeFolderModal}
        onSave={folderModal.isEditing ? handleUpdateFolder : handleCreateFolder}
        onDelete={handleDeleteFolder}
        folder={folderModal.folder}
        isEditing={folderModal.isEditing}
      />

      <ShareModal
        isOpen={shareModal.isOpen}
        onClose={closeShareModal}
        form={shareModal.form}
      />

      <CreateFormModal
        isOpen={createFormModal}
        onClose={() => setCreateFormModal(false)}
        onSelectOption={handleCreateFormOption}
      />

      <TemplateSelectionModal
        isOpen={templateModal}
        onClose={() => setTemplateModal(false)}
        onSelectTemplate={handleTemplateSelection}
      />

      <AIGeneratorModal
        isOpen={aiGeneratorModal.isOpen}
        onClose={() => setAiGeneratorModal({ isOpen: false, mode: 'text' })}
        onGenerateForm={handleAIGeneration}
        initialMode={aiGeneratorModal.mode}
      />

      <FolderShareModal
        isOpen={folderShareModal.isOpen}
        onClose={() => setFolderShareModal({ isOpen: false, folder: null })}
        folder={folderShareModal.folder}
      />

      <UserShareModal
        isOpen={userShareModal.isOpen}
        onClose={closeUserShareModal}
        item={userShareModal.item}
        type={userShareModal.type}
      />

      {/* Move to Folder Modal */}
      {moveToFolderModal.isOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)'
          }}>
            <h2 style={{
              margin: '0 0 14px 0',
              fontSize: '18px',
              fontWeight: '600',
              color: '#1f2937'
            }}>
              Move "{moveToFolderModal.form?.title}" to Folder
            </h2>
            
            <p style={{
              margin: '0 0 20px 0',
              color: '#6b7280',
              fontSize: '13px'
            }}>
              Select a folder to move this form to:
            </p>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              marginBottom: '24px',
              maxHeight: '300px',
              overflowY: 'auto'
            }}>
              <button
                onClick={() => handleMoveToFolder(moveToFolderModal.form.id, null)}
                style={{
                  padding: '16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  background: 'white',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#1f2937',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.borderColor = '#8b5cf6';
                  e.target.style.background = '#f9fafb';
                }}
                onMouseOut={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.background = 'white';
                }}
              >
                📁 No Folder (Root)
              </button>
              
              {folders.map((folder) => (
                <button
                  key={folder.id}
                  onClick={() => handleMoveToFolder(moveToFolderModal.form.id, folder.id)}
                  style={{
                    padding: '14px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    background: 'white',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#1f2937',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.borderColor = '#8b5cf6';
                    e.target.style.background = '#f9fafb';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.background = 'white';
                  }}
                >
                  <span style={{
                    fontSize: '18px',
                    color: folder.color || '#6b7280'
                  }}>
                    📁
                  </span>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '13px' }}>{folder.name}</div>
                    <div style={{
                      fontSize: '11px',
                      color: '#6b7280',
                      marginTop: '2px'
                    }}>
                      {folder.form_count || 0} forms
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setMoveToFolderModal({ isOpen: false, form: null })}
                style={{
                  padding: '12px 24px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#6b7280',
                  cursor: 'pointer',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.background = '#f9fafb';
                }}
                onMouseOut={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.background = 'white';
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardContainer>
  );
};

export default Dashboard;